import {
  ConfirmedResellerIcon,
  EditIcon,
  EyeIcon,
  EyeOffIcon,
  InfluencerIcon,
  IntermediateResellerIcon,
  TrashAltIcon
} from "assets/icons";
import { GoBackHeaderTitle, If, Media } from "components";
import { toastServerError } from "helpers";
import { BoxShadowLayout, ContentLayout } from "layouts";
import { DirectUploadMedia } from "libs/directUpload";
import { useRefetchQuery } from "libs/reactQuery";
import {
  getProductCommissionValue,
  getRoundedDiscountedProductPrice,
  ProductBadge,
  useGetVariantsByIds
} from "modules/products";
import { useMemo } from "react";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { Album, Button, CommissionLabel, Icon, LoaderScreen } from "ui";

import { useDeletePack, useGetPack, useTogglePackActive } from "../../apis";
import { PACK_QUERY_KEY, PACKS_QUERY_KEY } from "../../constants";

import styles from "./PackInfo.module.scss";

const PackInfo = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { packId } = useParams();

  const refetchList = useRefetchQuery([PACKS_QUERY_KEY]);
  const refetchPack = useRefetchQuery([PACK_QUERY_KEY, packId ?? ""]);

  const { pack, isLoading } = useGetPack(packId);

  const variantIds = useMemo(() => {
    if (!pack) return [];
    return Array.from(
      new Set(
        pack.slots.flatMap((slot) =>
          slot.options.map((o) => o.productVariantId)
        )
      )
    );
  }, [pack]);

  const { variants } = useGetVariantsByIds(variantIds);
  const variantsById = useMemo(
    () => new Map(variants.map((v) => [v.id, v])),
    [variants]
  );

  const toggleMutation = useTogglePackActive({
    onSuccess: () => {
      void refetchPack();
      void refetchList();
    },
    onError: toastServerError
  });

  const deleteMutation = useDeletePack({
    onSuccess: () => {
      toast.success(t("Pack deleted"));
      void refetchList();
      navigate("/packs");
    },
    onError: toastServerError
  });

  const mediaList = useMemo<DirectUploadMedia[]>(() => {
    if (!pack) return [];
    const items: DirectUploadMedia[] = [];
    if (pack.preview) items.push(pack.preview);
    if (pack.media?.length) {
      pack.media.forEach((m) => {
        if (!items.find((i) => i.id === m.id)) items.push(m);
      });
    }
    if (!items.length && pack.image) items.push(pack.image);
    return items;
  }, [pack]);

  const handleDelete = () => {
    if (!pack) return;
    if (window.confirm(t("Delete this pack?"))) {
      deleteMutation.mutate(pack.id);
    }
  };

  if (isLoading || !pack) {
    return (
      <ContentLayout
        title={
          <GoBackHeaderTitle
            title={t("Pack")}
            onGoBack={() => navigate("/packs")}
          />
        }
      >
        <BoxShadowLayout>
          <div className={styles.packInfo__loader}>
            <LoaderScreen />
          </div>
        </BoxShadowLayout>
      </ContentLayout>
    );
  }

  const sortedSlots = [...pack.slots].sort((a, b) => a.position - b.position);

  return (
    <ContentLayout
      title={
        <GoBackHeaderTitle
          title={t("Pack")}
          onGoBack={() => navigate("/packs")}
        />
      }
      action={
        <div style={{ display: "flex", gap: 12 }}>
          <Button
            variant="default"
            onClick={() =>
              toggleMutation.mutate({
                packId: pack.id,
                isActive: !pack.isActive
              })
            }
            icon={
              pack.isActive ? (
                <EyeOffIcon fill={"#090914"} width={20} height={20} />
              ) : (
                <EyeIcon fill={"#090914"} width={20} height={20} />
              )
            }
          >
            {pack.isActive ? t("Hide") : t("Show")}
          </Button>
          <Button
            variant="default"
            onClick={() => navigate(`/packs/${pack.id}/edit`)}
            icon={<EditIcon fill={"#090914"} width={18} height={18} />}
          >
            {t("Edit")}
          </Button>
          <Button
            variant="delete"
            onClick={handleDelete}
            icon={<TrashAltIcon fill={"#FFFFFF"} width={18} height={18} />}
          >
            {t("Delete")}
          </Button>
        </div>
      }
    >
      <BoxShadowLayout>
        <div className={styles.packInfo}>
          <div className={styles.packInfo__mainInfoContainer}>
            <div className={styles.packInfo__mainInfoBlock}>
              {mediaList.length > 0 ? (
                <Album
                  mediaList={mediaList}
                  className={styles.packInfo__album}
                />
              ) : (
                <div className={styles.packInfo__albumPlaceholder}>
                  {t("No media uploaded yet")}
                </div>
              )}

              {pack.description && (
                <div>
                  <h3 className={styles.packInfo__sectionTitle}>
                    {t("Description")}
                  </h3>
                  <p className={styles.packInfo__description}>
                    {pack.description}
                  </p>
                </div>
              )}
            </div>

            <div className={styles.packInfo__mainInfoBlock}>
              <div>
                <div className={styles.packInfo__titleRow}>
                  <h1 className={styles.packInfo__title}>{pack.title}</h1>
                  {pack.badge && <ProductBadge badge={pack.badge} />}
                  {!pack.isActive && (
                    <span className={styles.packInfo__inactiveBadge}>
                      {t("Inactive")}
                    </span>
                  )}
                </div>

                <div className={styles.packInfo__pricesGrid}>
                  {[
                    {
                      title: t("Standard Price"),
                      icon: null,
                      fullPrice: pack.price,
                      tierCommission: pack.commission,
                      tierCommissionPercentage: pack.commissionPercentage
                    },
                    {
                      title: t("Intermediate Reseller Price"),
                      icon: IntermediateResellerIcon,
                      fullPrice: pack.intermediateResellerPrice || pack.price,
                      tierCommission: pack.commission,
                      tierCommissionPercentage: pack.commissionPercentage
                    },
                    {
                      title: t("Confirmed Reseller Price"),
                      icon: ConfirmedResellerIcon,
                      fullPrice: pack.confirmedResellerPrice || pack.price,
                      tierCommission: pack.commission,
                      tierCommissionPercentage: pack.commissionPercentage
                    },
                    {
                      title: t("Influencer Price"),
                      icon: InfluencerIcon,
                      fullPrice: pack.influencerPrice || pack.price,
                      tierCommission: pack.influencerCommission,
                      tierCommissionPercentage:
                        pack.influencerCommissionPercentage
                    }
                  ].map((tier) => {
                    const discountedPrice = getRoundedDiscountedProductPrice(
                      tier.fullPrice,
                      pack.discount,
                      pack.discountAmount
                    );
                    const commissionValue = getProductCommissionValue(
                      discountedPrice,
                      tier.tierCommission,
                      tier.tierCommissionPercentage
                    );

                    return (
                      <div key={tier.title}>
                        <div className={styles.packInfo__priceTitle}>
                          {tier.icon && <Icon as={tier.icon} boxSize={20} />}
                          {tier.title}:
                        </div>
                        <div className={styles.packInfo__priceContainer}>
                          <div className={styles.packInfo__actualPrice}>
                            {discountedPrice.toLocaleString("en-US")} FCFA
                          </div>
                          <If condition={tier.fullPrice !== discountedPrice}>
                            <div className={styles.packInfo__fullPrice}>
                              {tier.fullPrice.toLocaleString("en-US")} FCFA
                            </div>
                          </If>
                        </div>
                        <If condition={!pack.commission && commissionValue}>
                          <CommissionLabel commissionValue={commissionValue} />
                        </If>
                      </div>
                    );
                  })}
                </div>

                <If condition={pack.commission}>
                  <div className={styles.packInfo__fixedCommission}>
                    <CommissionLabel
                      commissionValue={pack.commission}
                      iconSize={20}
                      fontSize={16}
                      fixed
                    />
                  </div>
                </If>
              </div>

              <div className={styles.packInfo__separatorLine} />

              <div>
                <h3 className={styles.packInfo__sectionTitle}>
                  {t("Pack details")}
                </h3>
                <div className={styles.packInfo__detailsList}>
                  <div className={styles.packInfo__detailsRow}>
                    <span className={styles.packInfo__detailsLabel}>
                      {t("Short ID")}
                    </span>
                    <span className={styles.packInfo__detailsValue}>
                      {pack.shortId}
                    </span>
                  </div>
                  <div className={styles.packInfo__detailsRow}>
                    <span className={styles.packInfo__detailsLabel}>
                      {t("Slots")}
                    </span>
                    <span className={styles.packInfo__detailsValue}>
                      {pack.slots.length}
                    </span>
                  </div>
                  {(!!pack.discount || !!pack.discountAmount) && (
                    <div className={styles.packInfo__detailsRow}>
                      <span className={styles.packInfo__detailsLabel}>
                        {t("Discount")}
                      </span>
                      <span className={styles.packInfo__detailsValue}>
                        {pack.discount
                          ? `${pack.discount}%`
                          : `${pack.discountAmount.toLocaleString("en-US")} FCFA`}
                      </span>
                    </div>
                  )}
                  {pack.minBuyingPriceCombination > 0 && (
                    <div className={styles.packInfo__detailsRow}>
                      <span className={styles.packInfo__detailsLabel}>
                        {t("Buying Price")}
                      </span>
                      <span className={styles.packInfo__detailsValue}>
                        {pack.minBuyingPriceCombination.toLocaleString(
                          "en-US"
                        )}{" "}
                        FCFA
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className={styles.packInfo__separatorLine} />

          <div>
            <h3 className={styles.packInfo__sectionTitle}>{t("Slots")}</h3>
            <div className={styles.packInfo__slots}>
              {sortedSlots.map((slot, index) => {
                const sortedOptions = [...slot.options].sort(
                  (a, b) => a.position - b.position
                );
                return (
                  <div className={styles.packInfo__slot} key={slot.id}>
                    <div className={styles.packInfo__slotHeader}>
                      <span className={styles.packInfo__slotIndex}>
                        {t("Slot")} #{index + 1}
                      </span>
                      <span className={styles.packInfo__slotTitle}>
                        {slot.title}
                      </span>
                    </div>
                    <div className={styles.packInfo__slotOptions}>
                      {sortedOptions.map((option) => {
                        const variant = variantsById.get(
                          option.productVariantId
                        );
                        const thumb =
                          variant?.preview ?? variant?.compressedPreview;
                        const categoryId = variant?.product?.categoryId;
                        const canNavigate =
                          !!variant?.productId && !!categoryId;
                        return (
                          <div
                            className={styles.packInfo__slotOption}
                            key={option.id}
                            onClick={() => {
                              if (!canNavigate) return;
                              navigate(
                                `/products/details/${categoryId}/${variant!.productId}/${variant!.id}/boxes`,
                                {
                                  state: {
                                    from: `/packs/${pack!.id}`,
                                    fromLabel: t("Pack")
                                  }
                                }
                              );
                            }}
                            role={canNavigate ? "button" : undefined}
                            style={
                              canNavigate ? { cursor: "pointer" } : undefined
                            }
                          >
                            {thumb ? (
                              <Media
                                className={styles.packInfo__slotOptionThumb}
                                mediaFile={thumb}
                              />
                            ) : (
                              <div
                                className={styles.packInfo__slotOptionThumb}
                              />
                            )}
                            <div className={styles.packInfo__slotOptionName}>
                              {variant?.name ?? option.productVariantId}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </BoxShadowLayout>
    </ContentLayout>
  );
};

export default PackInfo;
